import {
  IconName,
  LookbackOption,
  cleanupInitialFilterDateRange,
  useAccordionFilterBuilder,
  useGenericFilter,
  useStrategiesContext,
  type GenericFilter,
} from '@talos/kyoko';
import { useCallback, useMemo, useState } from 'react';
import { useGetPreTradeHistoryQuery } from '../../redux/preTradeApi';
import type { PreTradeResponseData } from '../../redux/preTradeApiTypes';
import { formatStrategyDisplayName } from '../../utils';

export interface HistoryFilter extends GenericFilter {
  Symbol?: string[];
  Strategy?: string[];
}

const SYMBOL_FILTER_KEY = 'Symbol' satisfies keyof HistoryFilter;
const STRATEGY_FILTER_KEY = 'Strategy' satisfies keyof HistoryFilter;
export const DEFAULT_HISTORY_FILTER: HistoryFilter = { _start: LookbackOption.PastMonth };
// 10000 is arbitrary but the same as we have on WS
// TODO: convert to WS like 99% of our blotters are and we can remove this
export const HISTORY_QUERY_LIMIT = '10000';

interface UseHistoryFilterOptions {
  initialFilter?: HistoryFilter;
  onFilterChange?: (filter: HistoryFilter) => void;
}

export function useHistoryFilter({
  initialFilter = DEFAULT_HISTORY_FILTER,
  onFilterChange,
}: UseHistoryFilterOptions = {}) {
  const { strategiesByName } = useStrategiesContext();

  const [queryDates, setQueryDates] = useState<{ StartDate?: string; EndDate?: string }>(() => {
    const resolved = cleanupInitialFilterDateRange(initialFilter);
    return { StartDate: resolved.StartDate, EndDate: resolved.EndDate };
  });

  const { data: scenarios = [], isLoading: isHistoryDataLoading } = useGetPreTradeHistoryQuery({
    ...queryDates,
    Limit: HISTORY_QUERY_LIMIT,
  });

  const filterableProperties = useMemo(() => {
    // Deduplicate since multiple scenarios can share the same Symbol/Strategy
    const symbols = [...new Set(scenarios.map(s => s.Symbol))].toSorted();
    const strategies = [...new Set(scenarios.map(s => s.Strategy))].toSorted();
    return [
      {
        key: SYMBOL_FILTER_KEY,
        label: SYMBOL_FILTER_KEY,
        field: SYMBOL_FILTER_KEY,
        icon: IconName.AtSymbol,
        options: symbols,
        getOptionLabel: (option: string) => option,
      },
      {
        key: STRATEGY_FILTER_KEY,
        label: STRATEGY_FILTER_KEY,
        field: STRATEGY_FILTER_KEY,
        icon: IconName.ArrowUpDown,
        options: strategies,
        getOptionLabel: (option: string) => formatStrategyDisplayName(option, strategiesByName) ?? option,
      },
    ];
  }, [scenarios, strategiesByName]);

  const onFilterChanged = useCallback(
    (filter: HistoryFilter) => {
      // Symbol/Strategy are filtered client-side — only re-fetch when the date range changes.
      setQueryDates(prev => {
        if (prev.StartDate === filter.StartDate && prev.EndDate === filter.EndDate) {
          return prev;
        }
        return { StartDate: filter.StartDate, EndDate: filter.EndDate };
      });
      onFilterChange?.(filter);
    },
    [onFilterChange]
  );

  const { filter, filterBuilderProps, blotterTableFilterProps } = useGenericFilter<HistoryFilter>(
    filterableProperties,
    {
      initialFilter,
      onFilterChanged,
    }
  );

  const filterBuilderAccordion = useAccordionFilterBuilder({ filterBuilderProps });

  const filteredScenarios = useMemo(() => filterScenariosBySessionGroup(scenarios, filter), [scenarios, filter]);

  return { filteredScenarios, isHistoryDataLoading, blotterTableFilterProps, filterBuilderAccordion };
}

/**
 * Filters scenarios by Symbol and/or Strategy at the session level.
 * A session is included if at least one of its scenarios matches all active filters (Symbol AND Strategy).
 */
export function filterScenariosBySessionGroup(
  scenarios: PreTradeResponseData[],
  { Symbol, Strategy }: Pick<HistoryFilter, 'Symbol' | 'Strategy'>
): PreTradeResponseData[] {
  const symbols = Symbol?.length ? new Set(Symbol) : undefined;
  const strategies = Strategy?.length ? new Set(Strategy) : undefined;

  if (!symbols && !strategies) {
    return scenarios;
  }

  // Find sessions where at least one scenario matches both filters
  const matchingSessions = new Set(
    scenarios
      .filter(s => (!symbols || symbols.has(s.Symbol)) && (!strategies || strategies.has(s.Strategy)))
      .map(s => s.SessionIdentifier)
  );

  return scenarios.filter(s => matchingSessions.has(s.SessionIdentifier));
}
